// @givmo/board — EVERY word officer roles layer 1 adds, in one place: who is a director, and which
// officer titles a person holds (CBG-YR3-ROLES, D-CBG-21).
//
// ══ WHICH WORDS ARE APPROVED ════════════════════════════════════════════════════════════════════
//
// All of them. Jonathan approved the officer-roles mockups for building on 2026-10-07 ("Approved")
// with every word kept a draft, and then the words themselves in copy round CBG-YR3-ROLES-COPY1 on
// 2026-10-12 13:49 PDT ("otherwise approved"): every line here as written, but the circulate
// preview's first sentence, which takes the page's suggestion; "What everyone on the roster gets"
// changed in CBG-YR6-COPY1's next part (2026-10-16), and again with the circulate preview's
// not-asked line in its next part after that (cut outside's words, 2026-10-17 06:55 PDT, "go with
// your recs"); and the on-behalf signature's line and the closed session's participant note in
// CBG-FIX104 (2026-10-17 17:42 PDT, "1 and 2, approved."), each line says so. A line added or
// changed since is marked DRAFT where it sits, until its own round. Each line names the frame that
// draws it (`l1-…`), or "not drawn" for a state no frame shows.
//
// ⚠ Plan §3.2 and §3.4 bind every line: what the roster records, never a conclusion. A title is a
// label the organization chose: no line says what a title may do or what the law asks of it. No
// pronoun for a named person.

export const officerRolesWords = {
  // ── The two facts (the member dialogs, the member page) ──────────────────────────────────────

  // Drawn, l1-member-add and l1-former-member (the choice's label).
  director: 'Director',
  // Drawn, l1-member-add and l1-former-member.
  yes: 'Yes',
  // Drawn, l1-member-add and l1-former-member.
  no: 'No',
  // Drawn, l1-member-add, l1-former-member and l1-member-maren.
  titles: 'Titles',
  // Drawn, l1-member-add and l1-former-member.
  directorMeans: 'Directors vote, count toward quorum and sign written consents.',
  // Drawn, l1-member-add (it replaces "Descriptive only — every seat has the same access in
  // Givmo."), and l1-member-maren / l1-member-ruth (closing the card). Changed in CBG-YR6-COPY1's
  // next part (CBG-YR6-MEET, not drawn; approved 2026-10-16): a committee's meeting sends its packet
  // and invitations to the committee's members only, so the line approved before ("Everyone on the
  // roster gets the same packets, records and meeting invitations, and files the annual
  // conflict-of-interest disclosure.") names the board's meetings.
  // Changed again in its next part after that (CBG-YR6-OUTSIDE, not drawn; approved 2026-10-17):
  // the roster now holds people who aren't on the board, whom none of this reaches, so the line
  // approved on 2026-10-16 ("Everyone on the roster gets the same records, …") says whom it means.
  sameAccess:
    'Everyone serving on the board gets the same records, the packets and invitations for the board’s meetings, and files the yearly conflicts of interest form.',
  // Not drawn: the board's titles are still loading.
  titlesLoading: 'Loading your board’s titles…',
  // Not drawn: editing a member while the titles couldn't be read; the rest of the form saves and
  // the person's titles stay as they are.
  titlesUnavailable:
    'We couldn’t load your board’s titles, so this saves without changing them. Try again later.',
  // Not drawn: adding a member, or recording a former one, while the titles couldn't be read:
  // they're recorded with none (a former member's can't be added later).
  titlesUnavailableNew:
    'We couldn’t load your board’s titles, so this records the member without any. Try again later to include them.',

  // ── The former-member dialog ──────────────────────────────────────────────────────────────────

  // Drawn, l1-former-member (it replaces "For a director whose service…").
  formerIntro:
    'For someone whose service had already ended before your board used Givmo. They are recorded as a former member straight away — no link is ever sent, and nothing about this gives them access.',
  // Drawn, l1-former-member.
  formerTitlesEnd: 'Any title you tick ends on the “Served to” date below.',

  // ── The roster ────────────────────────────────────────────────────────────────────────────────

  // Drawn, l1-roster (the column headings; "Member" and "Status" stay).
  columnDirector: 'Director',
  // The Director column's cell, on both rosters (CBG-UX-COPY2's A10: one table, one set of words;
  // the phone's stacked capacity line keeps "Director · Chair").
  directorCellYes: 'Yes',
  directorCellNo: 'No',
  columnTitles: 'Titles',
  columnTerm: 'Term',
  // Drawn, l1-roster ("5 directors · 6 serving · 1 former"; it keeps the shipped "6 serving · 1
  // former" and adds the directors). With no former member the mockups' own words drop "· 0 former"
  // (not drawn).
  count: (directors: number, serving: number, former: number) =>
    `${directors} ${directors === 1 ? 'director' : 'directors'} · ${serving} serving${former > 0 ? ` · ${former} former` : ''}`,

  // ── One line naming a person's facts (`capacityLine`) ─────────────────────────────────────────

  // Drawn, l1-member-ruth, l1-attendance and l1-circulate ("Director · Chair").
  directorWithTitles: (titles: string) => `Director · ${titles}`,
  // Drawn, l1-attendance and l1-circulate ("Director").
  directorAlone: 'Director',
  // Drawn, l1-member-maren and l1-attendance ("Secretary and Treasurer · not a director").
  titlesNotDirector: (titles: string) => `${titles} · not a director`,
  // Not drawn: someone on the roster with neither fact.
  notDirector: 'Not a director',
  // Drawn, l1-member-maren ("Secretary and Treasurer"; "A, B and C" for three).
  and: 'and',

  // ── The member page ───────────────────────────────────────────────────────────────────────────

  // Drawn, l1-member-maren and l1-member-ruth.
  card: 'Director and titles',
  // Drawn, l1-member-ruth.
  isDirectorMeans: 'Votes, counts toward quorum and signs written consents.',
  // Drawn, l1-member-maren.
  notDirectorMeans: 'Doesn’t vote, count toward quorum or sign written consents.',
  // Not drawn: someone who holds no title.
  noTitles: 'No titles.',
  // Drawn, l1-member-maren ("Secretary title carried over from the secretary seat") and
  // l1-member-ruth (the chair seat). The seat word is the one the title came from, so a title the
  // board renames later keeps the seat it came from.
  carriedOver: (label: string, seat: string) => `${label} title carried over from the ${seat} seat`,
  // Drawn, l1-member-maren ("Recorded as not a director, and titles changed, by Maya Solberg").
  historyNotDirector: 'Recorded as not a director',
  // Not drawn: a seat recorded as a director again.
  historyDirector: 'Recorded as a director',
  // Drawn, l1-member-maren (the second half of the line above).
  historyTitlesChanged: 'titles changed',
  // Not drawn: an edit that changed only the titles ("Titles changed by …", the shipped field
  // words' pattern).
  fieldTitles: 'titles',
  // Not drawn: the director fact's field word, for an update whose answer doesn't say which way it
  // went ("Director status and titles changed by …"; the backend always says, so a reader sees it
  // only on an answer missing that value).
  fieldDirector: 'director status',

  // ── A written consent, read by someone who isn't asked to sign it ─────────────────────────────

  // Drawn, l1-consent-nondirector.
  signedByDirectors: 'Signed by the board’s directors',
  // Drawn, l1-consent-nondirector.
  notAskedToSign: 'You can read it; you aren’t asked to sign it.',
  // Drawn, l1-consent-nondirector ("Version 1 · every signature names this exact wording, by its
  // fingerprint"; a non-signer has no signature of their own).
  everySignatureNames: 'every signature names this exact wording, by its fingerprint',

  // ── Putting a consent to the board (the circulate preview) ────────────────────────────────────

  // Drawn, l1-circulate ("directors" for "seats"), with the copy round's suggestion for its first
  // sentence: what is recorded, not what a consent accomplishes (the frames' "It becomes an act of
  // your board when …").
  circulateIntro:
    'It’s recorded as signed once every one of these directors has signed. The list is taken now and then fixed: a director added afterwards can’t sign this consent, and one who leaves afterwards is still one of the signatures it needs.',
  // Drawn, l1-circulate ("— 5 directors").
  circulateWho: (count: number) =>
    `Who will sign, as your roster stands now — ${count} ${count === 1 ? 'director' : 'directors'}`,
  // Not drawn: a roster with no serving director.
  circulateNoDirectors:
    'There are no serving directors on this board’s roster, so there is nobody for this consent to go to. Record your directors on the roster first.',
  // Drawn, l1-circulate ("Maren Lindqvist isn't a director and isn't asked to sign. Everyone on the
  // roster can read it."); the plural is not drawn. Its second sentence changed in CBG-YR6-COPY1's
  // next part (CBG-YR6-OUTSIDE, not drawn; approved 2026-10-17), since the roster now holds people
  // who aren't on the board and can't read the board's consent ("Everyone on the roster can read
  // it." before).
  circulateNotAsked: (names: string, count: number) =>
    `${names} ${count === 1 ? 'isn’t a director and isn’t' : 'aren’t directors and aren’t'} asked to sign. Everyone serving on the board can read it.`,

  // ── In the meeting ────────────────────────────────────────────────────────────────────────────

  // Drawn, l1-vote ("Maren Lindqvist isn't a director, so there's no vote to enter."); the plural
  // is not drawn.
  voteNotDirectors: (names: string, count: number) =>
    `${names} ${count === 1 ? 'isn’t a director' : 'aren’t directors'}, so there’s no vote to enter.`,

  // ── Words that assumed every seat was a director (the derivation's §2.4) ──────────────────────

  // Not drawn in layer 1; the mockups' word table (l2-onbehalf) gives it, replacing "Record a
  // signature on a director’s behalf" (the dialog's accessible name).
  onBehalfName: 'Record a signature on someone’s behalf',
  // As above, replacing "This records that a director signed these minutes somewhere other than
  // here …". Changed in CBG-FIX104 (the copy page's row 2.2, approved 2026-10-17 17:42 PDT): the
  // board's minutes' list leaves off committee members who aren't on the board, who are on the
  // roster, so the line no longer says whose roster.
  onBehalfBody:
    'This records that someone signed these minutes somewhere other than here — on paper, or in a message to you. It is recorded as your entry on their behalf, never as them signing in their own session.',
  // Not drawn, replacing "Recording a seat here is what lets that director read this session
  // afterwards, …": whoever is recorded in the room reads the session, director or not. Changed in
  // CBG-FIX104 (row 1.2, approved 2026-10-17 17:42 PDT), by the meeting's body: a committee member
  // who isn't on the board never reads a board meeting's closed session (D-CBG-25 as amended), and
  // reads a committee's only as one of that committee's members.
  sessionParticipantNote: (committeeMeeting: boolean) =>
    committeeMeeting
      ? 'Recording a seat here is what lets the person in it read this session afterwards, once their account is linked, if the seat is on the board or on the committee.'
      : 'Recording a seat here is what lets the person in it read this session afterwards, once their account is linked, if the seat is on the board.',

  // ── Acts anyone on the roster does, said without "the director" ───────────────────────────────
  // Signing the minutes, filing a conflict-of-interest disclosure and answering a meeting invitation
  // stay open to everyone on the roster (the derivation's §2.2), so their capture lines name where the
  // person acted, not that they are a director. A consent's own log keeps "Signed by the director":
  // only directors sign a consent.

  // Drawn, l2-signatures ("Signed in their own session, confirmed with a passkey"; the mockups'
  // word table found "Signed by the director" untrue for a non-director and sent it to this round).
  // The minutes' signature list.
  minutesOwnSession: 'Signed in their own session',
  // Not drawn: a disclosure the person confirmed themselves, in the minutes line's pattern; it
  // replaces "Confirmed by the director with …".
  disclosureOwnSession: (factor: string) => `Confirmed in their own session with ${factor}`,
  // Not drawn: a disclosure an officer recorded for the person; it replaces "… by an officer on the
  // director’s behalf" (the consent log's "on their behalf").
  officerOnTheirBehalf: 'by an officer on their behalf',
  // The same two on the reader's own page, said to them (CBG-UX-COPY2's A8): the officer's
  // register keeps the third person, which is about someone else.
  disclosureYouConfirmed: (factor: string) => `You confirmed it with ${factor}`,
  officerOnYourBehalf: 'by an officer on your behalf',
  // Not drawn: an invitation the person answered themselves, beside the shipped "Recorded on their
  // behalf"; it replaces "Answered by the director".
  answeredThemselves: 'Their own answer',
} as const;
